/**
 * Module launcher — the post-login landing surface (ADR-0049).
 *
 * A chrome-less, centered page: brand mark → greeting → whisper line
 * (the founder's 60-second read: overdue / approvals / next deadline) →
 * Search ⇄ Ask omnibox → quick actions → the modules grid. No
 * sidebar/topbar here — the workspace chrome appears once the user
 * enters a module (same pattern as `/pos`).
 *
 * Tiles come from `launcherTiles()` (derived from the sidebar's
 * `NAV_GROUPS`, so module entitlements + permissions gate identically).
 * Figures come from `briefings.today` + `briefings.workspaceSignals` —
 * deterministic server numbers, per "AI narrates; code computes".
 *
 * Omnibox modes:
 *   - Search — filters the module grid; Enter opens the first match.
 *   - Ask    — Enter stashes the prompt (`stashPendingPrompt`) and opens
 *              a new Ask Flint chat that auto-submits it. Hidden when the
 *              workspace lacks the `ai.assistant` module.
 *
 * @see prototype/explorations/module-launcher.html — design baseline
 * @see docs/adr/0037-module-launcher-landing.md
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Plus, RefreshCw, Search, Sparkles } from "lucide-react";
import { cn, Skeleton } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { meQueryOptions } from "../../../lib/session.ts";
import { formatLakhCrore } from "../../invoices/lib/format-money.ts";
import { stashPendingPrompt } from "../../ai-chat/index.ts";
import { AccountMenu } from "../components/account-menu.tsx";
import { BrandSquare } from "../components/brand-mark.tsx";
import { TeammateAvatar } from "../components/teammate-avatar.tsx";
import { UserAvatar } from "../components/user-avatar.tsx";
import { ThemeToggle } from "../theme/index.ts";
import { filterTiles, launcherTiles, visibleQuickActions } from "./launcher-config.ts";
import { TINT_CLASS } from "./tints.ts";
import "./launcher.css";

export function LauncherPage() {
  const navigate = useNavigate();
  const me = useQuery(meQueryOptions);
  const today = trpc.briefings.today.useQuery({});
  const signals = trpc.briefings.workspaceSignals.useQuery({});

  const company = me.data?.company ?? null;
  const companyName = company ? (company.tradeName ?? company.legalName) : null;
  const user = me.data?.user ?? null;
  const modules = company?.modules ?? [];
  const permissions = company?.permissions ?? [];
  const isOwner = permissions.includes("*");
  const can = useCallback(
    (permission: string) => isOwner || permissions.includes(permission),
    [isOwner, permissions],
  );
  const aiEnabled = modules.includes("ai.assistant");

  const tiles = useMemo(() => launcherTiles(modules, permissions), [modules, permissions]);
  const quickActions = useMemo(() => visibleQuickActions(permissions), [permissions]);

  // ── Omnibox state ──────────────────────────────────────────────────
  const inputRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<"search" | "ask">("search");
  const [query, setQuery] = useState("");
  const matches = useMemo(
    () => (mode === "search" ? filterTiles(tiles, query) : tiles),
    [mode, query, tiles],
  );

  // Ctrl/Cmd-K focuses the omnibox (page-scoped; the workspace Cmd-K
  // palette is a separate, later surface — see quick-search.tsx).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const submitOmnibox = useCallback(() => {
    const value = query.trim();
    if (!value) return;
    if (mode === "ask") {
      const chatId = stashPendingPrompt(value);
      void navigate({ to: "/ask/$chatId", params: { chatId } });
      return;
    }
    const first = filterTiles(tiles, value)[0];
    if (first) void navigate({ to: first.to });
  }, [mode, navigate, query, tiles]);

  // ── Whisper line (60-second read) ──────────────────────────────────
  const receivables = today.data?.receivables;
  const compliance = today.data?.compliance;
  const approvals = signals.data?.approvals;
  const overduePaise = BigInt(receivables?.overduePaise ?? "0");
  const whisperLoading = today.isLoading || signals.isLoading;
  // On fetch failure show nothing rather than a false "all clear".
  const whisperFailed = today.isError || signals.isError;
  const showOverdue = overduePaise > 0n && can("receipts.view");
  const showApprovals = (approvals?.pendingCount ?? 0) > 0;
  const showDeadline = !!compliance && can("gstReturns.view");

  const dateLine = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  // `html, body, #root` are locked to `overflow: hidden` for the workspace shell
  // (which scrolls inside its own region). The launcher lives OUTSIDE that shell,
  // so it owns its own vertical scroll: `h-dvh` + `overflow-y-auto` make this
  // container the scroll viewport instead of relying on the (locked) document
  // scroll. @see styles/index.css `html, body, #root`.
  return (
    <div className="h-dvh overflow-y-auto overflow-x-hidden bg-bg pb-16 text-ink">
      {/* ── Top strip — quiet; the workspace chrome starts inside modules ── */}
      <div className="flex h-[60px] items-center justify-between gap-3 px-6">
        <span className="inline-flex items-center gap-2 rounded-full border border-line bg-paper px-3 py-1.5 text-xs font-medium text-ink-2">
          <span aria-hidden className="size-[7px] rounded-full bg-ok" />
          {companyName ?? "…"}
        </span>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          {user && company ? (
            <AccountMenu user={user} company={company} side="bottom" align="end">
              <button
                type="button"
                className="flex items-center justify-center rounded-md transition-colors hover:bg-muted/10"
              >
                <UserAvatar name={user.name} src={user.avatarUrl} size="md" />
              </button>
            </AccountMenu>
          ) : null}
        </div>
      </div>

      <div className="mx-auto flex max-w-[1020px] flex-col items-center px-6">
        {/* ── Brand mark ── */}
        <div className="launcher-rise mt-[4.5vh]">
          <BrandSquare size="hero" className="launcher-mark" />
        </div>

        {/* ── Greeting ── */}
        <div className="launcher-rise d1 mt-[18px] text-center">
          <h1 className="text-3xl font-bold tracking-tight text-balance">
            {today.data?.greeting ?? "Welcome back"}
          </h1>
          <p className="mt-1.5 text-sm text-muted">
            {dateLine} · <span className="font-semibold text-ink-2">Flint</span>
            {companyName ? <> for {companyName}</> : null}
          </p>
        </div>

        {/* ── Whisper line — today at a glance ── */}
        <div
          className="launcher-rise d2 launcher-whisper mt-3.5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm"
          aria-label="Today at a glance"
        >
          {whisperLoading ? (
            <Skeleton className="h-4 w-72" />
          ) : whisperFailed ? null : (
            <>
              {showOverdue ? (
                <Link
                  to="/sales/collections"
                  className="inline-flex items-center gap-1.5 font-medium text-ink-2 hover:text-sky-700"
                >
                  <span aria-hidden className="size-2 rounded-[2.5px] bg-warn" />
                  <span className="font-semibold">{formatLakhCrore(receivables?.overduePaise)}</span>
                  overdue from {receivables?.overdueCustomerCount} customers
                </Link>
              ) : null}
              {showApprovals ? (
                <Link
                  to="/approvals"
                  className="inline-flex items-center gap-1.5 font-medium text-ink-2 hover:text-sky-700"
                >
                  <span aria-hidden className="size-2 rounded-[2.5px] bg-sky-600" />
                  <span className="font-semibold">{approvals?.pendingCount}</span>
                  {approvals?.pendingCount === 1 ? "approval" : "approvals"} waiting on you
                </Link>
              ) : null}
              {showDeadline ? (
                <Link
                  to="/compliance/gst-returns"
                  className="inline-flex items-center gap-1.5 font-medium text-ink-2 hover:text-sky-700"
                >
                  <span aria-hidden className="size-2 rounded-[2.5px] bg-bad" />
                  {compliance?.nextDeadline} in{" "}
                  <span className="font-semibold">{compliance?.daysUntil} days</span>
                </Link>
              ) : null}
              {!showOverdue && !showApprovals && !showDeadline ? (
                <span className="inline-flex items-center gap-1.5 font-medium text-muted">
                  <span aria-hidden className="size-2 rounded-[2.5px] bg-ok" />
                  All clear — nothing needs you right now
                </span>
              ) : null}
            </>
          )}
        </div>

        {/* ── Omnibox — Search ⇄ Ask ── */}
        <div className="launcher-rise d3 mt-6 w-full max-w-[680px]">
          <div
            className={cn(
              "flex w-full items-center gap-2.5 rounded-[14px] border border-line bg-paper py-2.5 pl-4 pr-2.5 shadow-md",
              "transition-colors focus-within:border-sky-600 focus-within:ring-4 focus-within:ring-sky-100",
            )}
          >
            {mode === "ask" ? (
              <Sparkles aria-hidden className="launcher-ask-lead size-[19px] shrink-0 text-sky-600" />
            ) : (
              <Search aria-hidden className="size-[19px] shrink-0 text-muted" />
            )}
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") submitOmnibox();
                if (event.key === "Escape") setQuery("");
              }}
              placeholder={
                mode === "ask"
                  ? "Ask Flint — “who hasn’t paid this month?”"
                  : "Search modules… or switch to Ask"
              }
              aria-label={mode === "ask" ? "Ask Flint" : "Search modules"}
              className="min-w-0 flex-1 bg-transparent text-base text-ink outline-none placeholder:text-muted"
            />
            {aiEnabled ? (
              <div
                role="group"
                aria-label="Omnibox mode"
                className="flex shrink-0 gap-0.5 rounded-[9px] border border-line bg-raised p-0.5"
              >
                <button
                  type="button"
                  aria-pressed={mode === "search"}
                  onClick={() => {
                    setMode("search");
                    inputRef.current?.focus();
                  }}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-[7px] px-3 py-1.5 text-xs font-medium transition-colors",
                    mode === "search" ? "bg-ink text-paper" : "text-muted hover:text-ink",
                  )}
                >
                  <Search aria-hidden className="size-3.5" />
                  Search
                </button>
                <button
                  type="button"
                  aria-pressed={mode === "ask"}
                  onClick={() => {
                    setMode("ask");
                    inputRef.current?.focus();
                  }}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-[7px] px-3 py-1.5 text-xs font-medium transition-colors",
                    mode === "ask" ? "bg-sky-600 text-paper" : "text-muted hover:text-ink",
                  )}
                >
                  <Sparkles aria-hidden className="size-3.5" />
                  Ask
                </button>
              </div>
            ) : null}
            <kbd className="hidden shrink-0 rounded-[5px] border border-line border-b-2 bg-raised px-1.5 py-0.5 font-mono text-xs text-muted sm:inline">
              Ctrl K
            </kbd>
          </div>

          {/* Quick actions — one-click creates (permission-gated) */}
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            {quickActions.map((action) => (
              <Link
                key={action.label}
                to={action.to}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border border-line bg-paper py-1.5 pl-2.5 pr-3.5",
                  "text-xs font-medium shadow-sm transition-colors hover:border-line-2 hover:bg-raised",
                  action.ai ? "text-sky-700" : "text-ink-2",
                )}
              >
                {action.ai ? (
                  <Sparkles aria-hidden className="size-3.5 text-sky-600" />
                ) : action.label === "Reconcile bank" ? (
                  <RefreshCw aria-hidden className="size-3.5 text-muted" />
                ) : (
                  <Plus aria-hidden className="size-3.5 text-muted" />
                )}
                {action.label}
              </Link>
            ))}
          </div>
        </div>

        {/* ── All modules ── */}
        <section className="launcher-rise d4 mt-10 w-full" aria-label="All modules">
          {matches.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted">
              No module matches “{query.trim()}”.
              {aiEnabled ? " Switch to Ask to put the question to Flint." : ""}
            </p>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(128px,1fr))] gap-2.5">
              {matches.map((tile, index) => {
                const Icon = tile.icon;
                return (
                  <Link
                    key={tile.key}
                    to={tile.to}
                    style={{ animationDelay: `${Math.min(300 + index * 18, 560)}ms` }}
                    className={cn(
                      "launcher-tile launcher-tile-in flex flex-col items-center gap-2 rounded-lg border border-line bg-paper px-2 pb-3 pt-4 shadow-sm",
                      "transition-all hover:-translate-y-0.5 hover:border-line-2 hover:shadow-md",
                      "motion-reduce:transition-none motion-reduce:hover:translate-y-0",
                    )}
                  >
                    {tile.persona ? (
                      <TeammateAvatar size={48} className="launcher-chip -my-1" />
                    ) : (
                      <span
                        className={cn(
                          "launcher-chip inline-flex size-10 items-center justify-center rounded-[10px]",
                          TINT_CLASS[tile.tint],
                        )}
                      >
                        <Icon aria-hidden className="size-5" />
                      </span>
                    )}
                    <span className="text-center text-sm font-semibold">{tile.label}</span>
                    {tile.sub ? (
                      <span className="-mt-1 text-center text-xs leading-snug text-muted">
                        {tile.sub}
                      </span>
                    ) : null}
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* ── Footer hints ── */}
        <div className="mt-11 flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-xs text-muted">
          <span>
            <kbd className="rounded border border-line border-b-2 bg-paper px-1 py-px font-mono text-xs">
              Ctrl K
            </kbd>{" "}
            focus search
          </span>
          <span>
            <kbd className="rounded border border-line border-b-2 bg-paper px-1 py-px font-mono text-xs">
              Enter
            </kbd>{" "}
            open first match
          </span>
        </div>
      </div>
    </div>
  );
}
